import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";
import { OnThisDay } from "../components/OnThisDay";
import { ComingUpCapsule } from "../components/ComingUpCapsule";
import { HorizonAggregates, type HorizonKey } from "../components/HorizonTabs";
import { L2RailShell, type L2NavItem } from "../components/L2Nav";
import { TodayClusters } from "../components/TodayClusters";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Sparkles,
  Sun,
  CalendarDays,
  CalendarRange,
  Inbox,
  ListTodo,
  Bot,
  Bell,
  Plus,
  RefreshCw,
  Calendar,
  AtSign,
  Lightbulb,
  X as XIcon,
  GraduationCap,
  MapPin,
} from "lucide-react";
import { api, type DashboardToday, type CaptureRow } from "../lib/api";
import { useAuth, getCsrfToken, refreshAuth } from "../lib/auth";
import { QuickCapture } from "../components/QuickCapture";
import { PushEnableBanner } from "../components/PushEnableBanner";
import { DeliveredBriefHero } from "../components/DeliveredBriefHero";
import { TodayAttentionStrip } from "../components/TodayAttentionStrip";
import { ObservationsPanel } from "../components/ObservationsPanel";
import { PrivateContentReviewPanel } from "../components/PrivateContentReviewPanel";
import { ViewFilter, useViewFilter } from "../components/ViewFilter";

const HORIZON_RAIL: ReadonlyArray<L2NavItem & { k: HorizonKey }> = [
  { k: "day", id: "day", label: "Today", Icon: Sun, hint: "What's in front of you right now." },
  { k: "week", id: "week", label: "This Week", Icon: CalendarDays, hint: "Aggregates Memso rolled up for the week." },
  { k: "month", id: "month", label: "This Month", Icon: CalendarRange, hint: "Aggregates Memso rolled up for the month." },
];

export function Today() {
  const auth = useAuth();
  const navigate = useNavigate();
  const params = useParams<{ handle?: string; horizon?: string }>();
  const handlePrefix = params.handle ? `/u/${params.handle}` : "";
  const activeHorizon: HorizonKey = (() => {
    const raw = params.horizon;
    if (raw === "week" || raw === "month") return raw;
    return "day";
  })();
  const setHorizon = useCallback(
    (next: string) => {
      const base = `${handlePrefix}/today`;
      navigate(next === "day" ? base : `${base}/horizon/${next}`);
    },
    [handlePrefix, navigate],
  );
  const [data, setData] = useState<DashboardToday | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Shared filter state across Today's snapshot row. Persisted under
  // the "today" view key in user_settings.view_filters so it survives
  // reloads. Only includeTest is wired to a list endpoint here today;
  // scope filtering on the dashboard summary stats lands in a follow-up
  // when /api/dashboard/today learns the scope arg.
  const { state: filter, setState: setFilter, loaded: filterLoaded } = useViewFilter("today");

  const refresh = useCallback(() => {
    setError(null);
    api
      .dashboardToday()
      .then((d) => setData(d))
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(refresh, [refresh]);
  useEffect(() => {
    const id = window.setInterval(refresh, 30_000);
    return () => window.clearInterval(id);
  }, [refresh]);

  const isEmpty =
    !!data &&
    data.stats.totalCaptures === 0 &&
    data.stats.openTasks === 0 &&
    data.stats.agentRunsToday === 0 &&
    data.stats.unreadMatches === 0;
  const firstName = (auth.user?.name || "").split(/\s+/)[0] || "there";

  const activeRailItem = HORIZON_RAIL.find((r) => r.k === activeHorizon) ?? HORIZON_RAIL[0];

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Today" }]} />
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {activeHorizon === "day"
              ? `${data?.greeting ?? "Hello"}, ${firstName}`
              : activeRailItem.label}
          </h1>
          <p className="text-base sm:text-sm text-slate-500 dark:text-slate-400">
            {activeHorizon === "day"
              ? `${data?.date ?? new Date().toISOString().slice(0, 10)} workspace`
              : activeRailItem.hint}
          </p>
          {activeHorizon === "day" && data?.context_now ? (
            <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {data.context_now}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {activeHorizon === "day" && <ViewFilter state={filter} onChange={setFilter} />}
          <button
            type="button"
            onClick={refresh}
            aria-label="Refresh"
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>
      </header>

      <L2RailShell
        items={HORIZON_RAIL}
        activeId={activeHorizon}
        onPick={setHorizon}
        railAriaLabel="Today horizon"
        listAriaLabel="Pick a horizon"
        tint="sky"
      >
        {activeHorizon === "day" ? (
          <div className="space-y-5">
            <PushEnableBanner />
            <DeliveredBriefHero />
            <TodayAttentionStrip />
            <TeachingBanner id="today_overview" />
            <TeachingBanner id="today_clusters" />

            <WelcomeBackCard firstName={firstName} />
            <QuickCapture onCaptured={refresh} />
            <ComingUpCapsule />

            {isEmpty ? (
              <WelcomeEmpty firstName={firstName} />
            ) : (
              <>
                <TodayClusters />

                <ContextSuggestions />
                <ObservationsPanel />
                <PrivateContentReviewPanel />
                <ReflectReminderCard show={!!data?.reflectReminder} />

                <Snapshots includeTest={filter.includeTest} filterLoaded={filterLoaded} />

                <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard
                    Icon={Inbox}
                    label="Captures today"
                    value={data?.stats.capturesToday ?? 0}
                    tint="sky"
                  />
                  <StatCard
                    Icon={ListTodo}
                    label="Open tasks"
                    value={data?.stats.openTasks ?? 0}
                    tint="emerald"
                  />
                  <StatCard
                    Icon={Bot}
                    label="Agent runs"
                    value={data?.stats.agentRunsToday ?? 0}
                    tint="violet"
                  />
                  <StatCard
                    Icon={Bell}
                    label="Unread matches"
                    value={data?.stats.unreadMatches ?? 0}
                    tint="amber"
                  />
                </section>

                <OnThisDay />
                <RecentRail recent={data?.recent ?? []} />
              </>
            )}

            {error && (
              <div className="rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-4 py-3 text-sm text-rose-800 dark:text-rose-200">
                Could not reach backend: {error}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Aggregates Memso rolled up for {activeHorizon === "week" ? "this week" : "this month"}.
            </p>
            <HorizonAggregates horizon={activeHorizon} />
          </div>
        )}
      </L2RailShell>
    </div>
  );
}

function ContextSuggestions() {
  const [items, setItems] = useState<
    { notification_id: string; title: string; body: string | null; reason: string }[]
  >([]);

  const load = useCallback(() => {
    api
      .contextPending()
      .then((d) =>
        setItems(
          d.notifications.map((n) => {
            let reason = "";
            try {
              reason = n.context_match_json
                ? (JSON.parse(n.context_match_json).reason as string) ?? ""
                : "";
            } catch {
              reason = "";
            }
            return {
              notification_id: n.notification_id,
              title: n.title,
              body: n.body,
              reason,
            };
          }),
        ),
      )
      .catch(() => setItems([]));
  }, []);

  useEffect(load, [load]);

  if (items.length === 0) return null;

  return (
    <section
      aria-label="Context suggestions"
      className="rounded-xl border border-sky-200 dark:border-sky-900 bg-sky-50/60 dark:bg-sky-950/30 p-4 space-y-2.5"
    >
      <h2 className="text-sm font-semibold text-sky-800 dark:text-sky-200 flex items-center gap-2">
        <MapPin className="w-4 h-4" /> Right now
      </h2>
      <ul className="space-y-2">
        {items.map((it) => (
          <li
            key={it.notification_id}
            className="flex items-start gap-3 rounded-lg bg-white dark:bg-slate-900 px-3 py-2.5"
          >
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {it.title}
              </div>
              {it.body ? (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {it.body}
                </div>
              ) : null}
              {it.reason ? (
                <div className="mt-1 inline-block text-[11px] rounded bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 px-1.5 py-0.5">
                  Suggested because: {it.reason}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={async () => {
                await api.contextDismiss(it.notification_id);
                load();
              }}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** First-visit welcome card. Only renders once per user; once
 *  dismissed (or auto-dismissed by clicking a quick action), we POST
 *  user_settings.shown_welcome=true so the card never returns. The
 *  GET on mount also doubles as the gate -- if shown_welcome is
 *  already true the card never paints. */
function WelcomeBackCard({ firstName }: { firstName: string }) {
  const [shouldShow, setShouldShow] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/user/settings/shown_welcome", { credentials: "same-origin" })
      .then((r) => (r.status === 404 ? null : r.json()))
      .then((b: { value?: unknown } | null) => {
        if (cancelled) return;
        setShouldShow(b?.value !== true);
      })
      .catch(() => {
        if (!cancelled) setShouldShow(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = async () => {
    setShouldShow(false);
    try {
      const csrf = getCsrfToken();
      await fetch("/api/user/settings", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ key: "shown_welcome", value: true }),
      });
      await refreshAuth();
    } catch {
      /* best-effort; the local dismissal sticks for this session
         even if the persist fails */
    }
  };

  if (!shouldShow) return null;

  return (
    <section className="glass rounded-2xl border border-sky-300/60 dark:border-sky-700/50 backdrop-blur bg-gradient-to-br from-sky-50/70 via-white/40 to-indigo-50/60 dark:from-sky-900/20 dark:via-slate-800/30 dark:to-indigo-900/20 p-5 sm:p-6 space-y-3">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl overflow-hidden ring-1 ring-sky-300/60 dark:ring-sky-600/60 shadow-sm flex-shrink-0">
          <img src="/icons/icon-192.png" alt="" className="w-full h-full object-cover" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
            Welcome to Memso, {firstName}.
          </h2>
          <p className="text-base sm:text-sm text-slate-600 dark:text-slate-300 mt-1">
            Capture what's on your mind. Connect your sources. Memso weaves it together so you
            can recall, synthesize, and act on what you know. Try one of the moves below.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 w-7 h-7 rounded-md inline-flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-700/40"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
        <WelcomeAction to="/capture" Icon={Plus} label="Capture" tint="emerald" onPick={dismiss} />
        <WelcomeAction to="/explore/recall" Icon={Sparkles} label="Recall" tint="violet" onPick={dismiss} />
        <WelcomeAction to="/explore/knowledge-graph" Icon={Calendar} label="Browse Weave" tint="sky" onPick={dismiss} />
        <WelcomeAction to="/settings?section=connectors" Icon={Bot} label="Connect sources" tint="amber" onPick={dismiss} />
      </div>
    </section>
  );
}

function WelcomeAction({
  to,
  Icon,
  label,
  tint,
  onPick,
}: {
  to: string;
  Icon: typeof Sparkles;
  label: string;
  tint: "emerald" | "violet" | "sky" | "amber";
  onPick: () => void;
}) {
  const tints: Record<string, string> = {
    emerald: "from-emerald-400 to-teal-500",
    violet: "from-violet-400 to-fuchsia-500",
    sky: "from-sky-400 to-indigo-500",
    amber: "from-amber-400 to-orange-500",
  };
  return (
    <Link
      to={to}
      onClick={onPick}
      className="group inline-flex items-center gap-2 rounded-xl bg-white/70 dark:bg-slate-800/60 hover:bg-white dark:hover:bg-slate-700/70 border border-slate-200/70 dark:border-slate-700/50 px-3 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 transition-colors"
    >
      <span className={`inline-flex w-7 h-7 rounded-lg items-center justify-center text-white shadow-sm bg-gradient-to-br ${tints[tint]}`}>
        <Icon className="w-3.5 h-3.5" />
      </span>
      {label}
    </Link>
  );
}

/** Three-card horizontal snapshot row pulling live data from the new
 *  anchors: People (recent interactions), Recall (recent
 *  conversations), and Weave (knowledge graph kind counts). Each
 *  card is a click-through into the relevant Explore pivot. Loads
 *  in parallel; each card handles its own empty + error state so
 *  one slow API doesn't block the whole row.
 *
 *  Memso lexicon throughout: "people you've heard from",
 *  "recent threads", "what's woven". */
function Snapshots({ includeTest, filterLoaded }: { includeTest: boolean; filterLoaded: boolean }) {
  const [people, setPeople] = useState<{ display_name: string; primary_email: string | null; last_interacted_at: string | null }[] | null>(null);
  const [conversations, setConversations] = useState<{ id: string; title: string | null; last_message_at: string }[] | null>(null);
  const [graph, setGraph] = useState<{ kind: string; n: number }[] | null>(null);
  const [insights, setInsights] = useState<{
    last_run_at: string | null;
    total_active_insights: number;
    insight_counts_by_kind: Record<string, number>;
  } | null>(null);

  useEffect(() => {
    if (!filterLoaded) return;
    api.peopleList({ sort: "recent", limit: 5, include_test: includeTest })
      .then((r) => setPeople(r.people.map((p) => ({ display_name: p.display_name, primary_email: p.primary_email, last_interacted_at: p.last_interacted_at }))))
      .catch(() => setPeople([]));
    api.conversationList(5, { include_test: includeTest })
      .then((r) => setConversations(r.conversations))
      .catch(() => setConversations([]));
    api.graphByKind()
      .then((r) => setGraph(r.kinds.slice(0, 6)))
      .catch(() => setGraph([]));
    api.synthesisStatus()
      .then((s) => setInsights({
        last_run_at: s.last_run_at,
        total_active_insights: s.total_active_insights,
        insight_counts_by_kind: s.insight_counts_by_kind,
      }))
      .catch(() => setInsights({ last_run_at: null, total_active_insights: 0, insight_counts_by_kind: {} }));
  }, [includeTest, filterLoaded]);

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" aria-label="Workspace snapshots">
      <SnapshotCard
        title="People"
        subtitle="who's come up recently"
        href="/explore/people"
        Icon={AtSign}
        tint="emerald"
        empty="People weave in as they show up across captures."
        rows={
          people === null
            ? null
            : people.map((p) => ({
                primary: p.display_name,
                secondary: p.last_interacted_at
                  ? new Date(p.last_interacted_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  : (p.primary_email || ""),
              }))
        }
      />
      <SnapshotCard
        title="Recall"
        subtitle="recent threads"
        href="/explore/recall"
        Icon={Sparkles}
        tint="violet"
        empty="Ask Memso to recall, weave, or synthesize."
        rows={
          conversations === null
            ? null
            : conversations.map((c) => ({
                primary: c.title || "Untitled thread",
                secondary: relativeTime(c.last_message_at),
              }))
        }
      />
      <SnapshotCard
        title="Weave"
        subtitle="what's connected"
        href="/explore/knowledge-graph"
        Icon={Lightbulb}
        tint="sky"
        empty="The weave grows as Memso extracts entities from your captures."
        rows={
          graph === null
            ? null
            : graph.map((k) => ({
                primary: k.kind,
                secondary: `${k.n}`,
              }))
        }
      />
      <SnapshotCard
        title="Memso noticed"
        subtitle={
          insights?.last_run_at
            ? `last weave ${relativeTime(insights.last_run_at)}`
            : "synthesis layer"
        }
        href="/explore/people"
        Icon={GraduationCap}
        tint="amber"
        empty="Nothing inferred yet. Capture a few packets and Memso will weave."
        rows={
          insights === null
            ? null
            : Object.entries(insights.insight_counts_by_kind)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([kind, n]) => ({
                  primary: kind.replace(/_/g, " "),
                  secondary: `${n}`,
                }))
        }
      />
    </section>
  );
}

function SnapshotCard({
  title,
  subtitle,
  href,
  Icon,
  tint,
  empty,
  rows,
}: {
  title: string;
  subtitle: string;
  href: string;
  Icon: typeof Sparkles;
  tint: "emerald" | "violet" | "sky" | "amber";
  empty: string;
  rows: { primary: string; secondary: string }[] | null;
}) {
  const tints: Record<string, string> = {
    emerald: "from-emerald-400 to-teal-500",
    violet: "from-violet-400 to-fuchsia-500",
    sky: "from-sky-400 to-indigo-500",
    amber: "from-amber-400 to-orange-500",
  };
  return (
    <Link
      to={href}
      className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 space-y-2 hover:border-slate-300 dark:hover:border-slate-600 transition-colors block"
    >
      <div className="flex items-center gap-2">
        <span className={`inline-flex w-7 h-7 rounded-lg items-center justify-center text-white shadow-sm bg-gradient-to-br ${tints[tint]}`}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-base sm:text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</div>
          <div className="text-xs sm:text-[11px] text-slate-500 dark:text-slate-400">{subtitle}</div>
        </div>
      </div>
      {rows === null ? (
        <div className="text-xs sm:text-[11px] text-slate-400 italic">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs sm:text-[11px] text-slate-500 dark:text-slate-400 italic">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2 text-sm sm:text-xs">
              <span className="flex-1 truncate text-slate-700 dark:text-slate-200">{r.primary}</span>
              <span className="text-xs sm:text-[11px] text-slate-500 dark:text-slate-400 flex-shrink-0">{r.secondary}</span>
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}

/** Friendly relative-time formatter for the snapshots. "Just now",
 *  "5m ago", "2h ago", "3d ago". Falls back to a short date for
 *  anything older than a week. */
function relativeTime(iso: string): string {
  if (!iso) return "";
  const ts = new Date(iso).getTime();
  if (!Number.isFinite(ts)) return "";
  const diff = Date.now() - ts;
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function WelcomeEmpty({ firstName }: { firstName: string }) {
  return (
    <section className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-8 sm:p-10 text-center space-y-4">
      <div className="mx-auto inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-indigo-500 items-center justify-center text-white shadow-md">
        <Sparkles className="w-7 h-7" strokeWidth={2.5} />
      </div>
      <div>
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
          Welcome, {firstName}.
        </h2>
        <p className="text-base sm:text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-md mx-auto">
          This is your day-one workspace. Capture a thought, a task, a person you met,
          a link worth saving. The brief, the weave, the daily nudges all grow from
          what you drop in here.
        </p>
      </div>
      <Link
        to="/capture"
        className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
      >
        <Plus className="w-4 h-4" />
        Capture your first thought
      </Link>
    </section>
  );
}

function RecentRail({ recent }: { recent: CaptureRow[] }) {
  if (recent.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm sm:text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
        Recent captures
      </h3>
      <ul className="space-y-1.5">
        {recent.map((r) => (
          <li
            key={r.id}
            className="glass rounded-lg px-3 py-2 border border-slate-200/70 dark:border-slate-700/50 backdrop-blur flex items-start gap-3"
          >
            <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 flex-shrink-0">
              {r.type}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-base sm:text-sm text-slate-900 dark:text-slate-100 truncate">
                {r.text}
              </div>
              <div className="text-xs sm:text-[11px] text-slate-500 dark:text-slate-400">
                {new Date(r.captured_at).toLocaleTimeString()}
                {r.processed_at ? " · processed" : " · queued"}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function StatCard({
  Icon,
  label,
  value,
  tint,
}: {
  Icon: typeof Sparkles;
  label: string;
  value: number;
  tint: "sky" | "emerald" | "violet" | "amber";
}) {
  const tints: Record<typeof tint, string> = {
    sky: "text-sky-500 bg-sky-100 dark:bg-sky-900/30",
    emerald: "text-emerald-500 bg-emerald-100 dark:bg-emerald-900/30",
    violet: "text-violet-500 bg-violet-100 dark:bg-violet-900/30",
    amber: "text-amber-500 bg-amber-100 dark:bg-amber-900/30",
  };
  return (
    <div className="glass rounded-xl p-3 sm:p-4 border border-slate-200/70 dark:border-slate-700/50 backdrop-blur flex flex-col gap-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tints[tint]}`}>
        <Icon className="w-4 h-4" strokeWidth={2.5} />
      </div>
      <div>
        <div className="text-2xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
          {value}
        </div>
        <div className="text-xs sm:text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          {label}
        </div>
      </div>
    </div>
  );
}

function ReflectReminderCard({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <section
      aria-label="Time to reflect"
      className="glass rounded-2xl border border-indigo-300/60 dark:border-indigo-700/50 backdrop-blur bg-gradient-to-br from-indigo-50/70 via-white/40 to-violet-50/50 dark:from-indigo-900/20 dark:via-slate-800/30 dark:to-violet-900/15 p-5 flex items-center justify-between gap-3"
    >
      <div>
        <h2 className="text-sm font-bold text-indigo-800 dark:text-indigo-200">
          Time to reflect
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Two minutes on how today went compounds over time.
        </p>
      </div>
      <Link
        to="/reflect"
        className="shrink-0 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
      >
        Reflect now
      </Link>
    </section>
  );
}

